"use client"

import { cn } from "@/lib/utils"
import { INSPECTOR_PICKER_TRIGGER_CLASS } from "@/components/question-composer/composer-fields"
import {
  questionQualityTileCopy,
  type QuestionQualityCheck,
  type QuestionQualityHistory,
} from "@/lib/question-quality"

const TILE_WASH: Record<
  ReturnType<typeof questionQualityTileCopy>["tone"],
  string
> = {
  danger:
    "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--exxat-color-danger,var(--destructive))_8%,transparent),color-mix(in_oklch,var(--exxat-color-danger,var(--destructive))_3%,transparent))]",
  warning:
    "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--exxat-color-warning,var(--chart-4))_10%,transparent),color-mix(in_oklch,var(--exxat-color-warning,var(--chart-4))_3%,transparent))]",
  neutral:
    "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--brand-color)_7%,transparent),color-mix(in_oklch,var(--brand-color)_3%,transparent))]",
}

const TILE_ICON: Record<
  ReturnType<typeof questionQualityTileCopy>["tone"],
  string
> = {
  danger: "bg-[var(--exxat-color-danger,var(--destructive))]/10 text-[var(--exxat-color-danger,var(--destructive))]",
  warning: "bg-[var(--exxat-color-warning,var(--chart-4))]/15 text-foreground",
  neutral: "bg-brand/10 text-brand",
}

export function QuestionQualityTile({
  checks,
  history,
  disabled,
  onOpen,
}: {
  checks: QuestionQualityCheck[]
  history: QuestionQualityHistory | null
  disabled?: boolean
  onOpen: () => void
}) {
  const copy = questionQualityTileCopy(checks, history)
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium text-muted-foreground">Ask Leo</p>
      <button
        type="button"
        disabled={disabled}
        aria-label={
          copy.subtitle
            ? `Ask Leo: ${copy.title}. ${copy.subtitle}`
            : `Ask Leo: ${copy.title}`
        }
        onClick={onOpen}
        className={cn(
          INSPECTOR_PICKER_TRIGGER_CLASS,
          TILE_WASH[copy.tone],
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <span
          className={cn(
            "inline-flex size-9 shrink-0 items-center justify-center rounded-md",
            TILE_ICON[copy.tone],
          )}
          aria-hidden="true"
        >
          <i
            className="fa-duotone fa-solid fa-star-christmas text-base"
            aria-hidden="true"
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-foreground">
            {copy.title}
          </span>
          {copy.subtitle ? (
            <span className="truncate text-xs text-muted-foreground">
              {copy.subtitle}
            </span>
          ) : null}
        </span>
        <span
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground"
          aria-hidden="true"
        >
          <i className="fa-light fa-chevron-right text-xs" />
        </span>
      </button>
    </div>
  )
}
